import { useState, useEffect, SyntheticEvent } from "react";
import axios from "axios";
import { DateField, StatCard, type DataTableColumn } from "@routes-plan/ui";
import moment from "moment-timezone";
import { minYmd, nowOperationalDateString } from '../../utils/operationalDateTime';
import { useT } from "../../i18n";
import { setPageTitle } from "../../utils/pageTitle";
import { companyLocale, formatCurrency as companyMoney } from '../../utils/format/currency';
import { OfficeSelect, ReportEmpty, ReportFilters, ReportPage, ReportResults, ReportStats, ReportTable, RouteMultiSelect } from "./components/ReportLayout";

interface CashDifferenceRow {
  id: number | string;
  date: string;
  idNumber: string;
  name: string;
  route: string;
  routeId: number;
  cashDifference: number;
}

// Interfaces mínimas para filtros
interface Office { id: number; name: string; }
interface Route { id: number; name: string; }

interface FilterState {
  startDate: string;
  endDate: string;
  officeId: string;          // string para que no se limpie el Select
  selectedRouteIds: number[];
}

interface ReporteDescuadresProps {
  showAlert: (msg: string, severity?: "error" | "info" | "success" | "warning") => void;
  showLoading: () => void;
  hideLoading: () => void;
}

// Utils
const formatCurrency = (value: number): string =>
  companyMoney(value);

const formatDate = (dateString?: string): string => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString; // por si ya viene YYYY-MM-DD, lo dejamos
  return d.toLocaleDateString(companyLocale());
};

// Componente
const CashDifferences: React.FC<ReporteDescuadresProps> = ({
  showAlert, showLoading, hideLoading,
}) => {
  const t = useT();
  setPageTitle('menu.reports.cash-differences');
  const currentDate = nowOperationalDateString();

  const [offices, setOffices] = useState<Office[]>([]);
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [loading, setLoading] = useState(false);

  // filtro
  const [filter, setFilter] = useState<FilterState>({
    startDate: currentDate,
    endDate: currentDate,
    officeId: "",
    selectedRouteIds: [],
  });

  // datos UI
  const [rows, setRows] = useState<CashDifferenceRow[]>([]);
  const [queried, setQueried] = useState(false);
  const [totalCashDifference, setTotalCashDifference] = useState<number>(0);

  // Oficinas
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("/offices");
        if (data?.data) setOffices(data.data);
      } catch (e) {
        console.error(e);
        showAlert(t("summaryReports.loadOfficesError"), "error");
      }
    })();
  }, [showAlert, t]);

  // Cobros por oficina
  useEffect(() => {
    (async () => {
      if (!filter.officeId) return;
      setLoading(true);
      try {
        const { data } = await axios.get(`/offices/${filter.officeId}/routes`);
        setOfficeRoutes(data || []);
        setFilter((prev) => ({ ...prev, selectedRouteIds: [] }));
      } catch (e) {
        console.error(e);
        showAlert(t("summaryReports.loadRoutesError"), "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [filter.officeId, showAlert, t]);

  const validateForm = (): boolean => {
    if (!filter.officeId) {
      showAlert(t("summaryReports.selectOfficeFirst"), "warning");
      return false;
    }
    if (filter.selectedRouteIds.length === 0) {
      showAlert(t("summaryReports.selectAtLeastOneRoute"), "warning");
      return false;
    }
    if (new Date(filter.startDate) > new Date(filter.endDate)) {
      showAlert(t("summaryReports.startAfterEnd"), "warning");
      return false;
    }
    return true;
  };

  const columns: DataTableColumn<CashDifferenceRow>[] = [
    { id: 'date', header: t('common.date'), value: (r) => r.date, render: (r) => formatDate(r.date), csv: (r) => formatDate(r.date), sortable: true },
    { id: 'route', header: t('summaryReports.shared.route'), value: (r) => r.route, sortable: true },
    { id: 'idNumber', header: t('common.idNumber'), value: (r) => r.idNumber, sortable: true, hideBelow: 'sm' },
    { id: 'name', header: t('common.name'), value: (r) => r.name, sortable: true },
    { id: 'cashDifference', header: t('summaryReports.col.cashDifference'), value: (r) => Number(r.cashDifference ?? 0), render: (r) => formatCurrency(Number(r.cashDifference ?? 0)), align: 'right', sortable: true },
  ];

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    showLoading();
    setLoading(true);
    setQueried(true);
    try {
      const payload = {
        startDate: filter.startDate,      // "YYYY-MM-DD"
        endDate: filter.endDate,          // "YYYY-MM-DD"
        routeId: filter.selectedRouteIds,
      };

      const { data } = await axios.post("/reports/cash-differences", payload);

      const items: CashDifferenceRow[] = (data?.data || [])
        .map((item: any, index: number) => ({
          id: item.id ?? index,
          date: item.date,
          idNumber: item.idNumber,
          name: item.name,
          route: item.route,
          routeId: item.routeId,
          cashDifference: item.cashDifference,
        }))
        .sort((a: CashDifferenceRow, b: CashDifferenceRow) => {
          const aDate = moment(a.date as string);
          const bDate = moment(b.date as string);

          const byDateDesc = bDate.valueOf() - aDate.valueOf();
          if (byDateDesc !== 0) return byDateDesc;

          return String(a.route ?? "").localeCompare(String(b.route ?? ""), "es", {
            sensitivity: "base",
          });
        });

      if (!items.length) {
        setRows([]);
        setTotalCashDifference(0);
        showAlert(t("summaryReports.noRecordsInPeriod"), "info");
        return;
      }

      setRows(items);

      const total = items.reduce((acc, r) => acc + (r.cashDifference || 0), 0);
      setTotalCashDifference(total);

      showAlert(t("summaryReports.recordsFound", { count: items.length }), "success");
    } catch (error) {
      console.error("Error obteniendo datos:", error);
      showAlert(t("summaryReports.loadDataError"), "error");
    } finally {
      hideLoading();
      setLoading(false);
    }
  };

  return (
    <ReportPage title={t('summaryReports.cashDiff.title')} description={t('summaryReports.cashDiff.description')}>
      <ReportFilters
        onSubmit={() => handleSubmit({ preventDefault: () => undefined } as SyntheticEvent)}
        submitDisabled={loading}
        submitButtonProps={{ "data-tour": "cash-differences-query" }}
      >
        <DateField
          label={t("summaryReports.startDate")}
          data-tour="cash-differences-start"
          value={filter.startDate}
          onChange={(v) => setFilter((prev) => ({ ...prev, startDate: v ?? "" }))}
          max={minYmd(filter.endDate, nowOperationalDateString())}
          size="small"
          fullWidth
        />
        <DateField
          label={t("summaryReports.endDate")}
          data-tour="cash-differences-end"
          value={filter.endDate}
          onChange={(v) => setFilter((prev) => ({ ...prev, endDate: v ?? "" }))}
          min={filter.startDate}
          max={nowOperationalDateString()}
          size="small"
          fullWidth
        />
        <OfficeSelect offices={offices} value={filter.officeId} onChange={(id) => setFilter((prev) => ({ ...prev, officeId: id }))} tourId="cash-differences-office" />
        <RouteMultiSelect
          routes={officeRoutes}
          tourId="cash-differences-routes"
          value={filter.selectedRouteIds.map(String)}
          onChange={(ids) => setFilter((prev) => ({ ...prev, selectedRouteIds: ids.map(Number) }))}
        />
      </ReportFilters>

      {rows.length > 0 && (
        <ReportStats tourId="cash-differences-stats">
          <StatCard label={t('summaryReports.cashDiff.count')} value={rows.length} />
          <StatCard label={t('summaryReports.cashDiff.total')} value={formatCurrency(totalCashDifference)} />
        </ReportStats>
      )}

      <ReportResults>
        <ReportTable<CashDifferenceRow>
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading && queried}
          exportFileName={t('summaryReports.cashDiff.exportFile')}
          tourId="cash-differences"
          ariaLabel={t('summaryReports.cashDiff.title')}
          emptyState={<ReportEmpty queried={queried} />}
        />
      </ReportResults>
    </ReportPage>
  );
};

export default CashDifferences;
